import { IManagerProductOption, IManagerProduct } from '@/types';
import { useMemo } from 'react';

export interface UseManagerActiveProductParams {
  productOptions: IManagerProductOption[];
  activeProduct?: IManagerProduct | null;
}

/**
 * Custom hook to determine the active product value for the Manager Dashboard based
 * on the selected product and available options.
 *
 * This hook takes in the currently active product and a list of product options, and returns
 * the matched product option that corresponds to the active product's certification ID. If
 * there is no active product or if no matching option is found, it returns undefined.
 *
 * @param activeProduct - The currently active product, which may be null if no product is selected.
 * @param productOptions - An array of available product options to match against the active product.
 * @returns The matched product option corresponding to the active product, or undefined
 */
export function useManagerActiveProduct({
  productOptions,
  activeProduct,
}: UseManagerActiveProductParams): IManagerProductOption | undefined {
  const activeProductOption = useMemo(() => {
    if (!activeProduct) return undefined; // Return undefined if there is no active product

    // Find the product option that matches the active product's certification ID
    const activeProductMatch = productOptions.find(
      (option) => option.value === activeProduct?.certificationId?.toString(),
    );

    // Return the matched product option if found, otherwise return undefined
    return activeProductMatch ? activeProductMatch : undefined;
  }, [productOptions, activeProduct]);

  return activeProductOption;
}
